import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { sendAnalytics } from "@/utils/analytics";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";
import { showNotification } from "@/redux/actions/notification";
import { requestCloseSlideOver } from "@/redux/actions/slide_over";
import { CAPITAL_STACK_EVENTS } from "@/features/CapitalStack/events";
import { requestCloseModal } from "@/redux/actions/modal";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";

const REDUCER_PATH = "CapitalStackService";
const TAG = "CapitalStack";

export const dealCapitalStackLayerService = createApi({
  reducerPath: REDUCER_PATH,
  tagTypes: [TAG],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getDealCapitalStackLayers: builder.query({
      query: loanId => `loans/${loanId}/capital-stacks`,
      providesTags: () => [{ type: TAG, id: "LIST" }],
    }),
    getDealCapitalStackLayerById: builder.query({
      query: ({ loanId, dealCapitalStackLayerId }) => `loans/${loanId}/capital-stacks/${dealCapitalStackLayerId}`,
      providesTags: (result, error, { dealCapitalStackLayerId }) => [{ type: TAG, id: dealCapitalStackLayerId }],
    }),
    addCapitalStackLayer: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/capital-stacks`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: TAG, id: "LIST" }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Capital Stack Layer added successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          sendAnalytics(CAPITAL_STACK_EVENTS.CAPITAL_STACK_LAYER_ADDED, data);

          dispatch(requestCloseSlideOver());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateCapitalStackLayer: builder.mutation({
      query: ({ loanId, dealCapitalStackLayerId, ...body }) => ({
        url: `loans/${loanId}/capital-stacks/${dealCapitalStackLayerId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: TAG, id: "LIST" }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Capital Stack Layer updated successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          sendAnalytics(CAPITAL_STACK_EVENTS.CAPITAL_STACK_LAYER_UPDATED, data);

          dispatch(requestCloseSlideOver());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteCapitalStackLayer: builder.mutation({
      query: ({ loanId, dealCapitalStackLayerId, ...body }) => ({
        url: `loans/${loanId}/capital-stacks/${dealCapitalStackLayerId}`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: [{ type: TAG, id: "LIST" }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Capital Stack Layer deleted successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          sendAnalytics(CAPITAL_STACK_EVENTS.CAPITAL_STACK_LAYER_REMOVED, data);

          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),
    sortCapitalStackLayers: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/capital-stacks/sorting`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: TAG, id: "LIST" }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Capital Stack Layer deleted successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          sendAnalytics(CAPITAL_STACK_EVENTS.CAPITAL_STACK_LAYER_UPDATED, data);

          dispatch(requestCloseSlideOver());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useGetDealCapitalStackLayersQuery,
  useGetDealCapitalStackLayerByIdQuery,
  useAddCapitalStackLayerMutation,
  useUpdateCapitalStackLayerMutation,
  useDeleteCapitalStackLayerMutation,
  useSortCapitalStackLayersMutation,
} = dealCapitalStackLayerService;
